import React, {Fragment, useRef, useState} from 'react';
import cn from 'classnames';
import ReactDOM from 'react-dom';
import {t} from '@lingui/core/macro';

import {ILastResult} from 'features/http/model/HttpMonitor/ILastResult';
import {IStatusStyles, getStatusColor, getStatusIcon} from 'entities/location';


import styles from './HttpMonitorLocationsCounterStatus.module.scss';

const getTooltipStyle = (trigger: HTMLDivElement | null): React.CSSProperties => {
    const rect = trigger?.getBoundingClientRect();
    const base: React.CSSProperties = {
        position: 'fixed',
        left: (rect?.right ?? 0) + 8,
        zIndex: 9999,
    };
    if (window.location.pathname.endsWith('/details')) {
        return {...base, bottom: window.innerHeight - (rect?.bottom ?? 0)};
    }
    return {...base, top: (rect?.top ?? 0) - 5};
};

interface IHttpMonitorLocationsCounterStatusProps {
    title: string,
    count: number,
    totalLocationsCount: number,
    ok?: boolean | null,
    lastResult?: ILastResult[],
}

const HttpMonitorLocationsCounterStatus: React.FC<IHttpMonitorLocationsCounterStatusProps> = ({
    title,
    count,
    totalLocationsCount,
    ok,
    lastResult = [],
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);

    const hasTooltipData = lastResult && lastResult.length > 0;

    const openTooltip = () => {
        if (closeTimeoutRef.current !== null) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsHovered(true);
    };

    const scheduleClose = () => {
        if (closeTimeoutRef.current !== null) {
            window.clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = window.setTimeout(() => {
            setIsHovered(false);
            closeTimeoutRef.current = null;
        }, 120);
    };

    const containerClass = cn(
        styles.container,
        {
            [styles.someFailed]: ok === false,
            [styles.noFailed]: ok === true,
            [styles.unknownStatus]: ok === null,
        },
    );

    const statusStyles: IStatusStyles = {
        successColor: styles.successColor,
        errorColor: styles.errorColor,
        unknownColor: styles.unknownColor,
    };

    return (
        <div
            ref={triggerRef}
            className={containerClass}
            onMouseEnter={openTooltip}
            onMouseLeave={scheduleClose}
        >
            <span className={styles.text}>
                {count} из {totalLocationsCount}
            </span>
            {isHovered && ReactDOM.createPortal(
                <div
                    className={styles.tooltipContainer}
                    onMouseEnter={openTooltip}
                    onMouseLeave={scheduleClose}
                    style={getTooltipStyle(triggerRef.current)}
                >
                    <div className={styles.tooltipContent}>
                        {hasTooltipData ? (
                            <>
                                <div className={styles.title}>{title}</div>
                                <div className={styles.tooltipList}>
                                    <div className={cn(styles.columnHeader, styles.locationHeader)}>
                                        {t`Локация`}
                                    </div>
                                    <div className={styles.columnHeader}>
                                        {t`Результат`}
                                    </div>
                                    {lastResult.map((item, index) => (
                                        <Fragment key={index}>
                                            <span className={styles.iconCell}>{getStatusIcon(item.status)}</span>
                                            <span className={cn(
                                                styles.text,
                                                getStatusColor(item.status, statusStyles))}
                                            >
                                                {item.country}, {item.city}, {item.name}
                                            </span>
                                            <span className={cn(
                                                styles.text,
                                                getStatusColor(item.status, statusStyles))}
                                            >
                                                {item.message}
                                            </span>
                                        </Fragment>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.title}>{title}</div>
                                <div className={styles.noData}>
                                    {t`Нет данных о проверках`}
                                </div>
                            </>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    )
}

export default HttpMonitorLocationsCounterStatus;
